import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import blogData from '../data/blogs';

const Home = () => {
  const [featuredBlogs, setFeaturedBlogs] = useState([]);

  useEffect(() => {
    // Get latest 3 blogs for featured section
    const sortedBlogs = [...blogData].sort((a, b) => new Date(b.date) - new Date(a.date));
    setFeaturedBlogs(sortedBlogs.slice(0, 6));
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const featuredCalculators = [
    {
      title: 'Home Loan Calculator',
      description: 'Calculate your home loan EMI instantly with our advanced calculator',
      href: '/calculators/home-loan',
      icon: '🏠',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'EMI Calculator',
      description: 'Calculate EMI for any loan with flexible tenure options',
      href: '/calculators/emi',
      icon: '💰',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Tax Calculator',
      description: 'Maximize your tax savings with our comprehensive tax calculator',
      href: '/calculators/tax',
      icon: '📊',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'SIP Calculator',
      description: 'Plan your future with systematic investment planning',
      href: '/calculators/sip',
      icon: '📈',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const comparisonTools = [
    {
      title: 'Credit Card Comparison',
      description: 'Compare best credit cards with rewards, cashback, and benefits',
      href: '/comparisons/credit-cards',
      icon: '💳'
    },
    {
      title: 'Loan Comparison',
      description: 'Compare personal loans, home loans, and interest rates',
      href: '/comparisons/loans',
      icon: '🏦'
    },
    {
      title: 'Insurance Comparison',
      description: 'Compare health, life, and car insurance plans',
      href: '/comparisons/insurance',
      icon: '🛡️'
    }
  ];

  const guides = [
    {
      title: 'Tax Saving Tips',
      description: 'Learn effective strategies to reduce your tax burden legally',
      href: '/guides/tax-saving-tips',
      icon: '💡'
    },
    {
      title: 'Investment Strategies',
      description: 'Master the art of smart investing for long-term wealth',
      href: '/guides/investment-strategies',
      icon: '🎯'
    },
    {
      title: 'Stock Market Basics',
      description: 'Understand the fundamentals of stock market investing',
      href: '/guides/stock-market-basics',
      icon: '📚'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Finance Tools & Calculators for Smart Investments"
        description="Calculate EMI, plan investments, compare loans and credit cards. Free financial planning tools for smart investments and wealth building."
        keywords="finance calculator, EMI calculator, investment calculator, loan calculator, tax calculator, SIP calculator, financial planning, credit card comparison, loan comparison, insurance comparison"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-blue-600 to-indigo-700 py-24 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-300 opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300 opacity-5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block mb-6">
              <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                ✨ Your Financial Journey Starts Here
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
              Smart Financial Planning
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300 mt-2">
                Made Simple
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
              Calculate EMI, plan investments, compare financial products, and make informed 
              decisions with our comprehensive suite of financial tools and calculators.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/calculators/home-loan"
                className="group bg-white text-primary-600 font-bold text-lg px-8 py-4 rounded-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  Calculate EMI Now
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
              <Link
                to="/guides/investment-strategies"
                className="group border-2 border-white text-white font-bold text-lg px-8 py-4 rounded-xl hover:bg-white hover:text-primary-600 transform hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm bg-white/10"
              >
                <span className="flex items-center gap-2">
                  Learn Investment Tips
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
            </div>
            
            {/* Stats Section */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">10+</div>
                <div className="text-blue-200 text-sm">Calculators</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">25+</div>
                <div className="text-blue-200 text-sm">Blog Articles</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">100%</div>
                <div className="text-blue-200 text-sm">Free Tools</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl font-bold text-white">24/7</div>
                <div className="text-blue-200 text-sm">Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-indigo-100 text-indigo-600 px-4 py-2 rounded-full text-sm font-semibold">
                ⚡ Key Features
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Our Platform?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for smart financial planning in one place
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
                🚀
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Fast & Accurate</h3>
              <p className="text-gray-600 text-sm">Get instant calculations with precise results</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-green-50 to-white border border-green-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
                🔒
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">100% Secure</h3>
              <p className="text-gray-600 text-sm">Your data is safe and never stored</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-white border border-purple-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
                💯
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Completely Free</h3>
              <p className="text-gray-600 text-sm">All tools available without any cost</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
                📱
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mobile Friendly</h3>
              <p className="text-gray-600 text-sm">Works perfectly on all devices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Calculators */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-primary-100 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold">
                🧮 Powerful Tools
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Financial Calculators
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Make informed financial decisions with our comprehensive suite of calculators
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredCalculators.map((calculator, index) => (
              <Link
                key={calculator.title}
                to={calculator.href}
                className="group relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden"
              >
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${calculator.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-r ${calculator.color} rounded-xl flex items-center justify-center text-3xl mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    {calculator.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {calculator.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {calculator.description}
                  </p>
                  <div className="flex items-center text-primary-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                    Try Now
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Tools */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold">
                🔍 Smart Comparisons
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Compare Financial Products
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find the best financial products that suit your needs and budget
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {comparisonTools.map((tool) => (
              <Link
                key={tool.title}
                to={tool.href}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="text-5xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  {tool.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-5">
                  {tool.description}
                </p>
                <div className="flex items-center text-primary-600 font-semibold group-hover:translate-x-2 transition-transform">
                  Compare Now
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-teal-100 text-teal-600 px-4 py-2 rounded-full text-sm font-semibold">
                🎯 Simple Process
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in just three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200"></div>
            
            <div className="relative text-center">
              <div className="relative z-10 w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6 shadow-xl">
                1
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Choose Your Tool</h3>
              <p className="text-gray-600 leading-relaxed">
                Select from our wide range of financial calculators and comparison tools
              </p>
            </div>
            
            <div className="relative text-center">
              <div className="relative z-10 w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6 shadow-xl">
                2
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Enter Your Details</h3>
              <p className="text-gray-600 leading-relaxed">
                Fill in the required information and get instant, accurate calculations
              </p>
            </div>
            
            <div className="relative text-center">
              <div className="relative z-10 w-20 h-20 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-3xl font-bold text-white mx-auto mb-6 shadow-xl">
                3
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Get Results</h3>
              <p className="text-gray-600 leading-relaxed">
                View detailed results and make informed financial decisions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Guides */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-semibold">
                📚 Learn & Grow
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Financial Education Hub
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Learn from expert guides and strategies to build your financial knowledge
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {guides.map((guide) => (
              <Link
                key={guide.title}
                to={guide.href}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="text-5xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    {guide.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-5">
                    {guide.description}
                  </p>
                  <div className="flex items-center text-primary-600 font-semibold group-hover:translate-x-2 transition-transform">
                    Read Guide
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-pink-100 text-pink-600 px-4 py-2 rounded-full text-sm font-semibold">
                💬 User Reviews
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by thousands of users for their financial planning needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {'⭐'.repeat(5)}
                </div>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "This platform helped me calculate my home loan EMI accurately. The interface is clean and easy to use. Highly recommended!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  RK
                </div>
                <div>
                  <div className="font-bold text-gray-900">Rajesh Kumar</div>
                  <div className="text-sm text-gray-600">Home Buyer</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {'⭐'.repeat(5)}
                </div>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "The tax calculator saved me a lot of time during tax season. The blog articles are also very informative. Great resource!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  PS
                </div>
                <div>
                  <div className="font-bold text-gray-900">Priya Sharma</div>
                  <div className="text-sm text-gray-600">Software Engineer</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {'⭐'.repeat(5)}
                </div>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "I use the SIP calculator regularly to plan my investments. The comparison tools helped me choose the best credit card. Excellent platform!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  AM
                </div>
                <div>
                  <div className="font-bold text-gray-900">Amit Mehta</div>
                  <div className="text-sm text-gray-600">Investment Advisor</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {featuredBlogs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">
                  📝 Latest Articles
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Finance Blog
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Stay updated with the latest financial tips, strategies, and insights from our experts
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredBlogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100"
                >
                  {/* Blog Image */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300">
                    {blog.image ? (
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="192"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl">
                        📝
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                        {blog.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    <div className="flex items-center text-xs text-gray-500 mb-3">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(blog.date)}</span>
                      <span className="mx-2">•</span>
                      <span>{blog.readTime || '5 min read'}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">
                      {blog.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white font-bold text-xs">
                            {blog.author?.charAt(0) || 'F'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600 font-medium">{blog.author || 'Finance Team'}</span>
                      </div>
                      
                      <span className="text-primary-600 font-semibold text-sm group-hover:translate-x-1 transition-transform inline-flex items-center">
                        Read More
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>

                    {/* Tags */}
                    {blog.tags && blog.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {blog.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-medium"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            
            {/* View All Articles Button */}
            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="inline-flex items-center bg-primary-600 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl hover:bg-primary-700 transform hover:-translate-y-1 transition-all duration-300"
              >
                View All Articles
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-yellow-100 text-yellow-600 px-4 py-2 rounded-full text-sm font-semibold">
                ❓ Common Questions
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Find answers to the most common questions about our platform
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-primary-600 mr-3">Q.</span>
                Are these calculators free to use?
              </h3>
              <p className="text-gray-600 ml-8 leading-relaxed">
                Yes, absolutely! All our financial calculators and tools are completely free to use. There are no hidden charges or subscription fees.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-primary-600 mr-3">Q.</span>
                Is my personal information safe?
              </h3>
              <p className="text-gray-600 ml-8 leading-relaxed">
                Yes, your privacy is our top priority. We don't store any of your personal data or calculation inputs. All calculations are performed locally in your browser.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-primary-600 mr-3">Q.</span>
                How accurate are the calculations?
              </h3>
              <p className="text-gray-600 ml-8 leading-relaxed">
                Our calculators use industry-standard formulas and are regularly updated to ensure accuracy. However, results are for estimation purposes and should be verified with financial institutions.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-primary-600 mr-3">Q.</span>
                Can I use these tools on mobile devices?
              </h3>
              <p className="text-gray-600 ml-8 leading-relaxed">
                Absolutely! Our platform is fully responsive and works seamlessly on smartphones, tablets, and desktop computers. You can access all tools from any device.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <span className="text-primary-600 mr-3">Q.</span>
                How often is the blog content updated?
              </h3>
              <p className="text-gray-600 ml-8 leading-relaxed">
                We regularly publish new articles with the latest financial tips, strategies, and market insights. Check back frequently for fresh content!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-r from-primary-600 via-blue-600 to-indigo-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
              🚀 Get Started Now
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Start Your Financial Journey Today
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Take control of your finances with our comprehensive tools and expert guidance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/calculators/emi"
              className="group bg-white text-primary-600 font-bold text-lg px-8 py-4 rounded-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                Calculate Your EMI
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <Link
              to="/comparisons/credit-cards"
              className="group border-2 border-white text-white font-bold text-lg px-8 py-4 rounded-xl hover:bg-white hover:text-primary-600 transform hover:-translate-y-1 transition-all duration-300 backdrop-blur-sm bg-white/10"
            >
              <span className="flex items-center gap-2">
                Compare Credit Cards
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
